import { storyblokEditable } from "@storyblok/react";
import { motion } from "framer-motion";
import Image from "next/image";
import { FC, useEffect, useRef, useState } from "react";
import slugify from "react-slugify";

import BackgroundMedia from "@/components/elements/BackgroundMedia";
import Container from "@/components/elements/Container";
import HeadingLayout from "@/components/elements/Heading";
import Prose from "@/components/elements/Prose";
import Section from "@/components/elements/Section";
import StandardText, {
  StandardTextSize,
} from "@/components/elements/StandardText";
import { getStoryblokImageUrl } from "@/lib/utils";

export interface ImageTextBandProps {
  blok: any;
  pre_heading: string;
  heading: string;
  content: string;
  actions?: object;
  image: object;
  video: object;
  layout: string;
  hidden: boolean;
}

const BACKGROUNDS = {
  "": "",
  light: "bg-radial-gradient",
  dark: "",
  white: "",
};

function TextItem({ heading, content }) {
  const framerItem = {
    hidden: { opacity: 0, y: 25 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 75,
        mass: 0.75,
        duration: 0.35,
      },
    },
  };

  return (
    <motion.div variants={framerItem} className="space-y-2">
      <HeadingLayout size="2xs" weight="medium">
        {heading}
      </HeadingLayout>
      <Prose content={content} size="sm" />
    </motion.div>
  );
}

function Crossfade({ images }) {
  const [index, setIndex] = useState(0);
  const timer = useRef<any>();

  useEffect(() => {
    if (images.length > 1) {
      timer.current = setInterval(() => {
        setIndex((prev) => (images.length + prev + 1) % images.length);
      }, 7000);
    }
    return () => {
      clearInterval(timer.current);
    };
  }, [images]);

  return (
    <>
      {images.map((img, i) => (
        <Image
          key={img?.id}
          alt={img?.alt}
          src={getStoryblokImageUrl(img?.filename, 1200, 0)}
          className={`absolute object-cover duration-1000 h-screen ${
            index === i ? "opacity-100" : "opacity-0"
          }`}
          width={1200}
          height={1200}
        />
      ))}
    </>
  );
}

const ImageTextBand: FC<ImageTextBandProps> = ({
  blok,
  heading = blok.heading,
  image = blok.image,
  video = blok.video,
  layout = blok.layout,
  hidden = blok.hidden,
}) => {
  let container: string;
  let columnContent: string;
  let columnImage: string;
  let aspectImage: string;

  columnImage = "";

  switch (layout) {
    case "left":
      container = "flex lg:grid lg:grid-columns-2 gap-24";
      columnContent = "w-full relative space-y-8";
      aspectImage = "lg:h-screen lg:sticky top-0";
      // contentSize = 'base';
      break;
    case "right":
      container =
        "w-full flex flex-col lg:grid grid-rows-2 lg:grid-rows-1 lg:grid-cols-2 gap-24";
      columnContent = "w-full order-1 lg:order-0 space-y-12 self-center";
      aspectImage = "h-screen lg:sticky top-0 order-0 lg:order-1 ";
      // contentSize = 'base';
      break;
    default:
      container = "flex flex-col-reverse items-end sm:flex-col-reverse";
      columnContent = "mb-12";
      columnImage = "";
      aspectImage = "h-full";
      // contentSize = 'xl';
      break;
  }

  const framerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.35,
      },
    },
  };

  if (hidden) {
    return <></>;
  }

  return (
    <Section
      id={slugify(heading)}
      {...storyblokEditable(blok)}
      gapTop="lg"
      gapBottom="2xl"
      className={`background-${blok.background} ${
        BACKGROUNDS[blok.background]}`}
    >
      <Container size="wider">
        <div className={`${container} justify-stretch`}>
          <div className={`${columnContent}`}>
            <StandardText blok={blok} size={StandardTextSize.MD} />
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={framerContainer}
              className="grid grid-cols-1 gap-12 2xl:grid-cols-2"
            >
              {blok.items &&
                blok.items.map((b) => <TextItem key={b._uid} {...b} />)}
            </motion.div>
          </div>
          <div className={`${columnImage}`}>
            <div className={`${aspectImage}`}>
              <motion.div
                initial={{ opacity: 0, y: "25%" }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                  },
                }}
                viewport={{ once: true, margin: "200px" }}
                className="relative inset-0"
              >
                {image.length > 0 && <Crossfade images={image} />}
                {video.id && <BackgroundMedia image={video} />}
              </motion.div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default ImageTextBand;
